import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    TemplateRef,
    ViewEncapsulation,
    computed,
    input,
    model,
    output
} from '@angular/core';
import { TreeTableModule } from 'primeng/treetable';
import { FcpTooltipDirective, FcpTooltipPosition } from '../fcp-tooltip/fcp-tooltip.directive';

@Component({
    selector: 'fcp-tree-table',
    standalone: true,
    imports: [CommonModule, TreeTableModule, FcpTooltipDirective],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.data-e2e-hook]': 'e2e()'
    },
    templateUrl: './fcp-tree-table.component.html',
    styleUrls: ['./fcp-tree-table.component.less']
})
export class FcpTreeTableComponent {
    readonly FcpTreeTableSortOrder = FcpTreeTableSortOrder;
    readonly FcpTreeTableSelectionMode = FcpTreeTableSelectionMode;
    readonly FcpTooltipPosition = FcpTooltipPosition;

    e2e = input<string | null>(null);

    value = model<unknown[]>([]);
    columns = input<FcpTreeTableColumn[]>();
    loading = input<boolean>(false);
    styleClass = input<string>('fcp-tree-table');

    tableStyle = input<Record<string, unknown>>();
    tableStyleClass = input<string>();

    headerTemplate = input<TemplateRef<unknown>>();
    bodyTemplate = input<TemplateRef<unknown>>();
    cellTemplate = input<TemplateRef<unknown>>();
    footerTemplate = input<TemplateRef<unknown>>();
    emptyTemplate = input<TemplateRef<unknown>>();
    captionTemplate = input<TemplateRef<unknown>>();

    sortField = input<string | null>(null);
    sortOrder = input<FcpTreeTableSortOrder>(FcpTreeTableSortOrder.None);
    defaultSortOrder = input<FcpTreeTableSortOrder>(FcpTreeTableSortOrder.Ascending);
    customSort = input<boolean>(false);

    selectionMode = input<FcpTreeTableSelectionMode>(FcpTreeTableSelectionMode.Single);
    selectionKeys = input<FcpTreeTableSelectionKeys | FcpTreeTableCheckboxSelectionKeys | null>(null);
    metaKeySelection = input<boolean>(true);
    dataKey = input<string>('key');

    showExpandCollapseAll = input<boolean>(false);

    rowExpand = output<FcpTreeTableRowEvent>();
    rowCollapse = output<FcpTreeTableRowEvent>();
    selectionChange = output<FcpTreeTableSelectionKeys | FcpTreeTableCheckboxSelectionKeys | null>();
    sortChange = output<FcpTreeTableSortEvent>();
    rowClick = output<FcpTreeTableRowClickEvent>();
    toggleExpandAll = output<boolean>();

    protected readonly _shouldExpand = computed(() => !hasAnyExpanded(this.value() as FcpTreeNode[]));

    // Parent-provided selectionKeys. PrimeNG mutates that map in place before emitting, which does
    // not notify the input, so this stays on the last value the parent set — including updates
    // received outside checkbox mode.
    private readonly _boundSelectionKeys = computed(() => JSON.stringify(this.selectionKeys() ?? {}));

    onRowExpand(event: FcpTreeTableRowEvent) {
        this.rowExpand.emit(event);
        this.value.update((v) => [...v]);
    }

    onRowCollapse(event: FcpTreeTableRowEvent) {
        this.rowCollapse.emit(event);
        this.value.update((v) => [...v]);
    }

    toggleNode(node: FcpTreeNode, originalEvent: Event = new Event('click')): void {
        node.expanded = !node.expanded;
        if (node.expanded) {
            this.onRowExpand({ originalEvent, node });
        } else {
            this.onRowCollapse({ originalEvent, node });
        }
    }

    onSelectionChange(event: { originalEvent: Event; type: string; keys: FcpTreeTableSelectionKeys | null }) {
        this.selectionChange.emit(event.keys);
    }

    onSelectionKeysChange(keys: FcpTreeTableCheckboxSelectionKeys | null) {
        // Read before the mode guard so a non-checkbox parent update refreshes the snapshot.
        const boundSelectionKeys = this._boundSelectionKeys();
        if (this.selectionMode() !== FcpTreeTableSelectionMode.Checkbox) {
            return;
        }

        const serialized = JSON.stringify(keys ?? {});
        if (serialized === boundSelectionKeys) {
            return;
        }

        // Copy so callers get a new reference. The emitted map is the one PrimeNG just mutated.
        this.selectionChange.emit(keys ? { ...keys } : {});
    }

    onSort(event: { field: string; order: number }) {
        this.sortChange.emit({ field: event.field, order: event.order as FcpTreeTableSortOrder });
    }

    onRowClick(rowNode: {
        node: { data: Record<string, unknown>; parent?: { data: Record<string, unknown> } };
        level: number;
    }) {
        this.rowClick.emit({ node: rowNode.node, level: rowNode.level });
    }

    onToggleExpandAll(event: Event): void {
        event.stopPropagation();
        const expand = this._shouldExpand();
        this.value.update((nodes) => setAllExpanded(nodes as FcpTreeNode[], expand));
        this.toggleExpandAll.emit(expand);
    }
}

export interface FcpTreeNode {
    expanded?: boolean;
    children?: FcpTreeNode[];
    [key: string]: unknown;
}

export function setAllExpanded<T extends FcpTreeNode>(nodes: T[], expanded: boolean): T[] {
    return nodes.map((n) => ({
        ...n,
        expanded,
        children: n.children ? setAllExpanded(n.children, expanded) : undefined
    }));
}

export function hasAnyExpanded(nodes: FcpTreeNode[]): boolean {
    return nodes.some((n) => {
        if (!n.children?.length) return false;
        return n.expanded || hasAnyExpanded(n.children);
    });
}

export interface FcpTreeTableColumn {
    field: string;
    header: string;
    expander?: boolean;
    sortable?: boolean;
    width?: string;
    styleClass?: string;
    headerStyle?: Record<string, unknown>;
    bodyStyle?: Record<string, unknown>;
    headerSuffix?: string;
    headerTooltip?: string;
}

export enum FcpTreeTableSortOrder {
    Ascending = 1,
    Descending = -1,
    None = 0
}

export interface FcpTreeTableSortEvent {
    field: string;
    order: FcpTreeTableSortOrder;
}

export enum FcpTreeTableSelectionMode {
    Single = 'single',
    Multiple = 'multiple',
    Checkbox = 'checkbox'
}

export type FcpTreeTableSelectionKeys = {
    [key: string]: boolean;
};

export interface FcpTreeTableCheckboxNodeState {
    checked?: boolean;
    partialChecked?: boolean;
}

export type FcpTreeTableCheckboxSelectionKeys = Record<string, FcpTreeTableCheckboxNodeState>;

export interface FcpTreeTableRowEvent {
    originalEvent: Event;
    node: unknown;
}

export interface FcpTreeTableRowClickEvent {
    node: {
        data: Record<string, unknown>;
        parent?: { data: Record<string, unknown> };
        children?: unknown[];
    };
    level: number;
}
